import React, { useCallback } from 'react';
import { HashLink } from 'react-router-hash-link';
import styled, { css } from 'styled-components/macro';
import { withRouter } from 'react-router';

import { LinkWithSearchParams as RouterLink } from 'components/base/Link/Link';
import { linkStyling } from 'styles/base-classes';

import { ReactComponent as IconExternal } from 'icons/icon-external.svg';
import { ReactComponent as Chevron } from 'icons/icon-chevron.svg';

const GreenIconExternal = styled(IconExternal)`
  color: ${({ theme }): string => theme.AccentGreen};
`;

const Link = styled(RouterLink)`
  ${linkStyling};
`;

const UncontrolledLink = styled('a')`
  ${linkStyling};

  svg {
    margin-left: 0.25em;
  }
`;

const chevron = ({ theme, withoutFocus }: any): any => css`
  svg {
    vertical-align: middle;
    width: ${theme.spacing.small};
    height: ${theme.spacing.small};
    margin-left: ${theme.spacing.small};
    color: ${theme.AccentGreen};
    transition: transform ${theme.transitionDuration};
    transition-timing-function: ${theme.timingFunction};
  }

  &:hover svg ${withoutFocus ? '' : ',&:focus svg '} {
    transition: transform ${theme.transitionDuration};
    transition-timing-function: ${theme.timingFunction};
    transform: translateX(0.25em);
  }
`;

const ChevronLink = styled(Link)`
  ${chevron};
`;

interface IAnchorProps {
  withChevron: boolean;
  to?: any;
  children: any;
  className: string;
  location: any;
  onClick: any;
}

const Anchor: React.FC<IAnchorProps> = ({
  withChevron,
  to,
  children,
  className,
  location,
  onClick,
  dataPw = 'anchor',
  ...rest
}): any => {
  const href = typeof to === 'object' ? to.pathname : to;
  const { hash, pathname } = new URL(href, window.location.origin);
  const isExternal = href.match(/^(https?:)?\/\//);
  const isEmail = href.indexOf('mailto:') >= 0;

  const onScroll = useCallback((el: any) => {
    const { top } = el.getBoundingClientRect();
    window.scrollBy(0, top - document.body.scrollTop - 100);
  }, []);

  if (isExternal) {
    return (
      <UncontrolledLink
        className={className}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-pw={dataPw}
      >
        {children}
        <GreenIconExternal />
      </UncontrolledLink>
    );
  }

  if (isEmail) {
    return (
      <UncontrolledLink className={className} href={href} data-pw={dataPw}>
        {children}
      </UncontrolledLink>
    );
  }

  const LinkComponent = withChevron ? ChevronLink : Link;

  if (hash || to.hasOwnProperty('hash')) {
    const StyledHashLink = LinkComponent.withComponent(HashLink);

    return (
      <StyledHashLink
        onClick={onClick}
        scroll={onScroll}
        className={className}
        data-pw={dataPw}
        to={
          to.hash
            ? to
            : {
                pathname: pathname,
                search: location.search,
                hash,
              }
        }
      >
        {children}
        {withChevron && <Chevron />}
      </StyledHashLink>
    );
  }

  return (
    <LinkComponent className={className} to={to} {...rest} onClick={onClick}>
      {children}
      {withChevron && <Chevron />}
    </LinkComponent>
  );
};

export default withRouter(Anchor as any) as any;
